import React from 'react';
import { getPathFromState, NavigationContainer } from '@react-navigation/native';
import { createStackNavigator } from '@react-navigation/stack';
import { createBottomTabNavigator } from '@react-navigation/bottom-tabs';

// Utils
import { camelPad } from '../utils/util_helpers';

// Configs
import ROUTES from './RouteKeys';

// Root Navigation
import * as RootNavigation from './RootNavigation';

// Screen Imports
import {
    NotFound,
    Blank,

    Welcome,
    Login,
    Register,
    ForgotPassword,

    Profile,
    ProfileEdit,
    ProfilePhotoEdit,
    Reviews,
    ReviewDetails,
    Menu,
    MenuItemDetails,
    ProfileSchedule,
    ProfileScheduleEdit,

    Dashboard,

    Orders,

    Catering,
    CateringDetails,
    CateringDetailsProposal,
    PastCatering,
    CateringExternalLead,
    HoodDetails,
    PastHood,

    Chat,
    ChatNewTopic,
    ChatDetails,

    Resources,
    ResourceDetails,

    More,

    AccountSettings
} from '../screens';
import SubscriptionTier from '../screens/AccountSettings/component_subscription_tier';

// Screen Options
const screenOptions = ({ route }) => ({
    title: route?.name === ROUTES.BLANK ? 'Truckster Vendor' : `${camelPad(route?.name?.replaceAll('_', ' '))} | Truckster Vendor`,
    headerShown: false,
    cardShadowEnabled: false,
    cardOverlayEnabled: false,
    gestureEnabled: false,
    animationEnabled: true
});

// Linking Configs
const linkingConfig = {
    config: {
        screens: {
            // General
            [ROUTES.BLANK]: '',
            [ROUTES.NOT_FOUND]: '*',
            // Login Stack
            [ROUTES.WELCOME]: 'welcome',
            [ROUTES.LOGIN]: 'login',
            [ROUTES.REGISTER]: 'register',
            [ROUTES.FORGOT_PASSWORD]: 'forgot-password',
            // Main Stack
            [ROUTES.MAIN]: 'main',
            // Profile Stack
            [ROUTES.PROFILE]: 'profile',
            [ROUTES.PROFILE_EDIT]: 'profile-edit',
            [ROUTES.PROFILE_EDIT_PHOTOS]: 'profile-edit-photos',
            [ROUTES.REVIEWS]: 'reviews',
            [ROUTES.REVIEW_DETAILS]: 'review-details',
            [ROUTES.MENU]: 'menu',
            [ROUTES.MENU_ITEM_DETAILS]: 'menu-food-item',
            [ROUTES.PROFILE_SCHEDULE]: 'schedule',
            [ROUTES.PROFILE_SCHEDULE_EDIT]: 'event-details',
            // Dashboard Stack
            [ROUTES.DASHBOARD]: 'dashboard',
            // Orders Stack
            [ROUTES.ORDERS]: 'orders',
            // Catering Stack
            [ROUTES.CATERING]: 'catering',
            [ROUTES.CATERING_EXTERNAL_LEAD]: 'catering-my-lead',
            // Chat Stack
            [ROUTES.CHAT]: 'chat',
            [ROUTES.CHAT_NEW]: 'chat/new-topic',
            [ROUTES.CHAT_DETAILS]: 'chat/topic-details',
            // Resources Stack
            [ROUTES.RESOURCES]: 'resources',
            [ROUTES.RESOURCE_DETAILS]: 'resource-details',
            // More Stack
            [ROUTES.MORE]: 'more',
            // Account Settings
            [ROUTES.ACCOUNT_SETTINGS]: 'account-settings',
            [ROUTES.SUBSCRIPTION_TIERS]: 'subscription-tiers'
        }
    },
    getPathFromState: (state, config) => {
        let path = getPathFromState(state, config)?.toLowerCase() || '';

        // Hide "/main"
        path = path.replace('/main', '');

        // Hide all passed params
        const paramIndex = path.indexOf('?');
        (paramIndex > -1) && (path = path.substring(0, paramIndex));

        // Hide the "/...stack" route names
        const stackIndex = path.indexOf('stack');
        (stackIndex > -1) && (path = path.substring(stackIndex + 5));

        // Return new path
        return path.toLowerCase();
    }
};

const Stack = createStackNavigator();

// 1. Login Stack
function CreateLoginStack() {
    return(
        <>
            <Stack.Screen name={ROUTES.WELCOME} component={Welcome} />
            <Stack.Screen name={ROUTES.LOGIN} component={Login} />
            <Stack.Screen name={ROUTES.REGISTER} component={Register} />
            <Stack.Screen name={ROUTES.FORGOT_PASSWORD} component={ForgotPassword} />
        </>
    );
}

// 2. Main Stack
const MainStack = createBottomTabNavigator();
function CreateMainStack() {
    return(
        <MainStack.Navigator
            initialRouteName={ROUTES.PROFILE_STACK}
            tabBar={() => false}
            screenOptions={({ ...screenOptions, lazy: true })}>
            {/* Profile Stack */}
            <MainStack.Screen name={ROUTES.PROFILE_STACK} component={CreateProfileStack} />

            {/* Dashboard Stack */}
            <MainStack.Screen name={ROUTES.DASHBOARD_STACK} component={CreateDashboardStack} />

            {/* Ordering Stack */}
            <MainStack.Screen name={ROUTES.ORDER_STACK} component={CreateOrderingStack} />

            {/* Catering Stack */}
            <MainStack.Screen name={ROUTES.CATERING_STACK} component={CreateCateringStack} />

            {/* Chat Stack */}
            <MainStack.Screen name={ROUTES.CHAT_STACK} component={CreateChatStack} />

            {/* Resources Stack */}
            <MainStack.Screen name={ROUTES.RESOURCES_STACK} component={CreateResourcesStack} />

            {/* More Stack */}
            <MainStack.Screen name={ROUTES.MORE_STACK} component={CreateMoreStack} />

            {/* Account Settings Stack */}
            <MainStack.Screen name={ROUTES.ACCOUNT_SETTINGS_STACK} component={CreateAccountSettingStack} />
        </MainStack.Navigator>
    );
}

// 2.1. Profile Stack
const ProfileStack = createStackNavigator();
function CreateProfileStack() {
    return(
        <ProfileStack.Navigator initialRouteName={ROUTES.PROFILE} screenOptions={screenOptions}>
            <ProfileStack.Screen name={ROUTES.PROFILE} component={Profile} />
            <ProfileStack.Screen name={ROUTES.PROFILE_EDIT} component={ProfileEdit} options={{ title: 'Edit Profile | Truckster Vendor' }} />
            <ProfileStack.Screen name={ROUTES.PROFILE_EDIT_PHOTOS} component={ProfilePhotoEdit} options={{ title: 'Truck Photo Album | Truckster Vendor' }} />
            <ProfileStack.Screen name={ROUTES.REVIEWS} component={Reviews} options={{ title: 'Reviews | Truckster Vendor' }} />
            <ProfileStack.Screen name={ROUTES.REVIEW_DETAILS} component={ReviewDetails} options={{ title: 'Review Details | Truckster Vendor' }} />
            <ProfileStack.Screen name={ROUTES.MENU} component={Menu} options={{ title: 'Menu | Truckster Vendor' }} />
            <ProfileStack.Screen name={ROUTES.MENU_ITEM_DETAILS} component={MenuItemDetails} options={{ title: 'Menu Item | Truckster Vendor' }} />
            <ProfileStack.Screen name={ROUTES.PROFILE_SCHEDULE} component={ProfileSchedule} options={{ title: 'Schedule | Truckster Vendor' }} />
            <ProfileStack.Screen name={ROUTES.PROFILE_SCHEDULE_EDIT} component={ProfileScheduleEdit} options={{ title: 'Event Details | Truckster Vendor' }} />
        </ProfileStack.Navigator>
    );
}

// 2.2. Dashboard Stack
const DashboardStack = createStackNavigator();
function CreateDashboardStack() {
    return(
        <DashboardStack.Navigator initialRouteName={ROUTES.DASHBOARD} screenOptions={screenOptions}>
            <DashboardStack.Screen name={ROUTES.DASHBOARD} component={Dashboard} />
        </DashboardStack.Navigator>
    );
}

// 2.3. Order Stack
const OrderingStack = createStackNavigator();
function CreateOrderingStack() {
    return(
        <OrderingStack.Navigator initialRouteName={ROUTES.ORDERS} screenOptions={screenOptions}>
            <OrderingStack.Screen name={ROUTES.ORDERS} component={Orders} />
        </OrderingStack.Navigator>
    );
}

// 2.4. Catering Stack
const CateringStack = createStackNavigator();
function CreateCateringStack() {
    return(
        <CateringStack.Navigator initialRouteName={ROUTES.CATERING} screenOptions={screenOptions}>
            <CateringStack.Screen name={ROUTES.CATERING} component={Catering} />
            <CateringStack.Screen name={ROUTES.CATERING_DETAILS} component={CateringDetails} />
            <CateringStack.Screen name={ROUTES.CATERING_DETAILS_PROPOSAL} component={CateringDetailsProposal} />
            <CateringStack.Screen name={ROUTES.CATERING_PAST} component={PastCatering} />
            <CateringStack.Screen name={ROUTES.CATERING_EXTERNAL_LEAD} component={CateringExternalLead} />
            <CateringStack.Screen name={ROUTES.HOOD_DETAILS} component={HoodDetails} />
            <CateringStack.Screen name={ROUTES.HOOD_PAST} component={PastHood} />
        </CateringStack.Navigator>
    );
}

// 2.5. Chat Stack
const ChatStack = createStackNavigator();
function CreateChatStack() {
    return(
        <ChatStack.Navigator initialRouteName={ROUTES.CHAT} screenOptions={screenOptions}>
            <ChatStack.Screen name={ROUTES.CHAT} component={Chat} />
            <ChatStack.Screen name={ROUTES.CHAT_NEW} component={ChatNewTopic} options={{ title: 'New Topic | Truckster Vendor' }} />
            <ChatStack.Screen name={ROUTES.CHAT_DETAILS} component={ChatDetails} options={{ title: 'Chat Topic | Truckster Vendor' }} />
        </ChatStack.Navigator>
    );
}

// 2.6. Resources Stack
const ResourcesStack = createStackNavigator();
function CreateResourcesStack() {
    return(
        <ResourcesStack.Navigator initialRouteName={ROUTES.RESOURCES} screenOptions={screenOptions}>
            <ResourcesStack.Screen name={ROUTES.RESOURCES} component={Resources} />
            <ResourcesStack.Screen name={ROUTES.RESOURCE_DETAILS} component={ResourceDetails} />
        </ResourcesStack.Navigator>
    );
}

// 2.7. More Stack
const MoreStack = createStackNavigator();
function CreateMoreStack() {
    return(
        <MoreStack.Navigator initialRouteName={ROUTES.MORE} screenOptions={screenOptions}>
            <MoreStack.Screen name={ROUTES.MORE} component={More} />
        </MoreStack.Navigator>
    );
}

// 2.8. Account Settings Stack
const AccountSettingStack = createStackNavigator();
function CreateAccountSettingStack() {
    return(
        <AccountSettingStack.Navigator initialRouteName={ROUTES.ACCOUNT_SETTINGS} screenOptions={screenOptions}>
            <AccountSettingStack.Screen name={ROUTES.ACCOUNT_SETTINGS} component={AccountSettings} />
            <AccountSettingStack.Screen name={ROUTES.SUBSCRIPTION_TIERS} component={SubscriptionTier} />
        </AccountSettingStack.Navigator>
    );
}

export default function RouterStack() {
    return(
        <NavigationContainer ref={RootNavigation.navigationRef} linking={linkingConfig}>
            <Stack.Navigator screenOptions={screenOptions}>
                {/* Blank / Loading Screen */}
                <Stack.Screen name={ROUTES.BLANK} component={Blank} />

                {/* Not Found Page */}
                <Stack.Screen name={ROUTES.NOT_FOUND} component={NotFound} />

                {/* Login Stack */}
                {CreateLoginStack()}

                {/* Main Stack */}
                <Stack.Screen name={ROUTES.MAIN} children={CreateMainStack} />
            </Stack.Navigator>
        </NavigationContainer>
    );
}
